/**
 * ActionPanel.tsx — Warm-amber "Azioni richieste" panel listing uncategorized
 * line items, with row selection + single/bulk category assignment. Wired to
 * the live tender-api: items come from useAuditLineItems({uncategorized:true}),
 * the category options from useAuditCategories, and assign is a real mutation.
 */
import { useMemo, useState } from "react";

import { I } from "../../components/icons";
import { type AuditCategory, type AuditLineItem } from "../../lib/tenderApi";
import { fmtEur } from "./helpers";
import { ItemBillPage, type ItemPagesView } from "./ItemBillPage";

interface CatOption {
  id: string;
  label: string;
}

/** Flatten categories + subcategories into assignable "code — name" options. */
function catOptions(categories: AuditCategory[]): CatOption[] {
  const out: CatOption[] = [];
  for (const c of categories) {
    if (c.isUncategorized) continue;
    out.push({ id: c.id, label: `${c.code ?? ""} — ${c.name}`.trim() });
    for (const s of c.subs) out.push({ id: s.id, label: `${s.code ?? ""} — ${s.name}`.trim() });
  }
  return out;
}

interface ActionPanelProps {
  items: AuditLineItem[];
  categories: AuditCategory[];
  selected: Set<string>;
  toggleSel: (id: string) => void;
  selectAll: (checked: boolean) => void;
  onAssign: (itemId: string, categoryId: string) => void;
  onBulkAssign: (categoryId: string) => void;
  busy: boolean;
  /** G3a — the bill page of each item; absent outside the Audit page (nothing is shown). */
  itemPages?: ItemPagesView;
}

function voci(n: number): string {
  return `${n.toLocaleString("it-IT")} ${n === 1 ? "voce" : "voci"}`;
}

export function ActionPanel({
  items,
  categories,
  selected,
  toggleSel,
  selectAll,
  onAssign,
  onBulkAssign,
  busy,
  itemPages,
}: ActionPanelProps): JSX.Element {
  const options = useMemo(() => catOptions(categories), [categories]);
  const nameById = useMemo(() => new Map(options.map((o) => [o.id, o.label])), [options]);
  const [bulkCat, setBulkCat] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  // Goal 55A, D19 and F-B: the sum of the amounts READ, and the unread ones counted apart —
  // an unread amount is «non determinabile», never a zero inside the sum (brief 2(b)).
  const unreadAmounts = items.filter((i) => i.total == null).length;
  const totalImporto = items.reduce((s, i) => s + (i.total ?? 0), 0);
  const importo =
    unreadAmounts === 0
      ? `Importo: ${fmtEur(totalImporto)}`
      : unreadAmounts === items.length
        ? `Importo non determinabile (${voci(unreadAmounts)})`
        : `Importo noto: ${fmtEur(totalImporto)} · importo non determinabile: ${voci(unreadAmounts)}`;

  return (
    <div
      className="card"
      style={{
        borderColor: "rgba(184,115,12,0.30)",
        boxShadow: "0 0 0 3px rgba(184,115,12,0.06)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "var(--c-warn-soft)",
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <I.Alert size={16} style={{ color: "var(--c-warn)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-warn)" }}>
            {items.length} voci da categorizzare
          </div>
          <div style={{ fontSize: 11.5, color: "rgba(184,115,12,0.85)" }}>
            {importo} · Vanno assegnate a categorie standard prima della Strategia
          </div>
        </div>
        {selected.size > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--c-warn)", fontWeight: 500 }}>
              {selected.size} selezionate
            </span>
            <select
              className="input"
              value={bulkCat}
              onChange={(e) => setBulkCat(e.target.value)}
              style={{ height: 28, fontSize: 12 }}
            >
              <option value="">Assegna a…</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (bulkCat) {
                  onBulkAssign(bulkCat);
                  setBulkCat("");
                }
              }}
              disabled={!bulkCat || busy}
            >
              Applica
            </button>
          </div>
        )}
      </div>
      <table className="tbl" style={{ fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                style={{ accentColor: "var(--c-warn)" }}
                checked={selected.size === items.length && items.length > 0}
                onChange={(e) => selectAll(e.target.checked)}
              />
            </th>
            <th style={{ width: 100 }}>ID</th>
            <th>Descrizione</th>
            <th style={{ textAlign: "right", width: 110 }}>Quantità</th>
            <th style={{ textAlign: "right", width: 110 }}>Totale</th>
            <th style={{ width: 230 }}>Categoria suggerita AI</th>
            <th style={{ width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => {
            const suggested = t.suggestedCategoryId ? nameById.get(t.suggestedCategoryId) : null;
            return (
              <tr key={t.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggleSel(t.id)}
                    style={{ accentColor: "var(--c-warn)" }}
                  />
                </td>
                <td className="t-mono" style={{ color: "var(--c-ink-3)", fontSize: 11.5 }}>
                  {t.code ?? t.id.slice(0, 8)}
                </td>
                <td style={{ color: "var(--c-ink)" }}>
                  {t.description}
                  {itemPages && (
                    <span style={{ marginLeft: 8 }}>
                      <ItemBillPage itemId={t.id} pages={itemPages} />
                    </span>
                  )}
                </td>
                <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-2)" }}>
                  {t.qty == null ? "—" : t.qty.toLocaleString("it-IT")}{" "}
                  <span className="muted">{t.unit}</span>
                </td>
                <td className="t-num" style={{ textAlign: "right", fontWeight: 500 }}>
                  {t.total == null ? "—" : fmtEur(t.total)}
                </td>
                <td>
                  {editingId === t.id ? (
                    <select
                      className="input"
                      autoFocus
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          onAssign(t.id, e.target.value);
                          setEditingId(null);
                        }
                      }}
                      style={{ height: 28, fontSize: 12, width: "100%" }}
                    >
                      <option value="">Scegli categoria…</option>
                      {options.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : suggested ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="ai-badge">
                        <I.Sparkle size={9} /> AI
                      </span>
                      <span style={{ fontSize: 12, color: "var(--c-ink-2)" }}>{suggested}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "var(--c-ink-3)" }}>—</span>
                  )}
                </td>
                <td>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: "var(--c-ok-soft)",
                        color: "var(--c-ok)",
                        borderColor: "rgba(47,125,78,0.20)",
                      }}
                      disabled={!t.suggestedCategoryId || busy}
                      onClick={() => t.suggestedCategoryId && onAssign(t.id, t.suggestedCategoryId)}
                    >
                      <I.Check size={12} /> Accetta
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEditingId((cur) => (cur === t.id ? null : t.id))}
                    >
                      <I.Edit size={12} /> Modifica
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}>
                <I.CheckC size={20} style={{ color: "var(--c-ok)", marginBottom: 6 }} />
                <div>Tutte le voci sono state categorizzate.</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
